// RU-SCANPERF SP-10: every signal one scanner produced, its outcome and this user's realised P&L.
// Read-only: GET /signals/scanner/:scanner/performance (sorted and paginated on the server, 50 per page).
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { scannerName } from "@/components/ScannerLink";

export const PERIODS = [
  { key: "7", label: "7D" },
  { key: "30", label: "30D" },
  { key: "90", label: "90D" },
  { key: "all", label: "All" },
];

export const OUTCOME_LABELS = {
  target_hit: "Target hit",
  stop_hit:   "Stop hit",
  expired:    "Expired",
  superseded: "Superseded",
  open:       "Open",
  other:      "Other",
};

const OUTCOME_TONE = {
  target_hit: "txt-profit",
  stop_hit:   "txt-loss",
  expired:    "txt-muted",
  superseded: "txt-muted",
  open:       "text-amber-400",
  other:      "txt-muted",
};

const DASH = "—";

export function fmtIst(iso) {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return `${d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  })} IST`;
}

export function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return DASH;
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}₹${abs}`;
}

const tone = (v) => (v > 0 ? "txt-profit" : v < 0 ? "txt-loss" : "txt-secondary");

function fmtPrice(v) {
  if (v === null || v === undefined || v === "") return DASH;
  const n = Number(v);
  return Number.isNaN(n) ? DASH : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function instrumentText(r) {
  const t = (r.instrument_type || "EQ").toUpperCase();
  if (t === "OPT" && r.option_strike != null && r.option_kind) {
    const exp = r.option_expiry
      ? ` · ${new Date(`${r.option_expiry}T00:00:00+05:30`).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" })}`
      : "";
    return `OPT ${r.option_strike} ${r.option_kind}${exp}`;
  }
  return t;
}

function executedText(e) {
  if (!e) return DASH;
  if (e.paper && e.live) return "Paper + Live";
  if (e.paper) return "Paper";
  if (e.live) return "Live";
  return DASH;
}

function Tile({ label, value, sub, valueClass = "text-[var(--text-primary)]", testid }) {
  return (
    <div data-testid={testid} className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1 min-w-0">
      <div className="text-[10px] font-section txt-muted tracking-wider uppercase">{label}</div>
      <div className={`text-xl sm:text-2xl font-mono-data font-bold [overflow-wrap:anywhere] ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] txt-muted font-mono-data [overflow-wrap:anywhere]">{sub}</div>}
    </div>
  );
}

const COLUMNS = [
  { h: "Time (IST)", sort: "time" },
  { h: "Symbol", sort: "symbol" },
  { h: "Instrument", sort: "instrument" },
  { h: "Dir", sort: "direction" },
  { h: "Entry / Stop / Target" },
  { h: "Conf.", sort: "confidence" },
  { h: "Outcome", sort: "outcome" },
  { h: "Executed" },
  { h: "Paper P&L", sort: "pnl_paper" },
  { h: "Live P&L", sort: "pnl_live" },
];

export default function ScannerPerformance() {
  const { scanner } = useParams();
  const [days, setDays] = useState("30");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: "time", order: "desc" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(`/signals/scanner/${encodeURIComponent(scanner)}/performance`,
            { params: { days, page, sort: sort.key, order: sort.order } })
      .then(({ data: d }) => setData(d))
      .catch((e) => {
        setData(null);
        setError(e?.response?.status === 404 ? "notfound" : "failed");
      })
      .finally(() => setLoading(false));
  }, [scanner, days, page, sort]);

  useEffect(() => { load(); }, [load]);

  const name = scannerName(scanner);
  const s = data?.summary;
  const p = s?.paper || {};
  const lv = s?.live || {};
  const o = s?.outcomes || {};

  function changePeriod(k) {
    setPage(1);
    setDays(k);
  }

  function changeSort(key) {
    setPage(1);
    setSort((cur) => (cur.key === key ? { key, order: cur.order === "desc" ? "asc" : "desc" } : { key, order: "desc" }));
  }

  return (
    <AppShell>
      <PageHeader
        overline="Scanner performance"
        title={name}
        description={`Every signal the ${name} scanner produced, its outcome and your realised P&L.`}
        actions={(
          <div data-testid="scanperf-periods" className="flex gap-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm p-1">
            {PERIODS.map(({ key, label }) => (
              <button key={key} type="button" data-testid={`scanperf-period-${key}`} aria-pressed={days === key}
                      onClick={() => changePeriod(key)}
                      className={`px-3 py-1 text-xs font-section rounded-sm transition-colors ${
                        days === key ? "bg-[var(--bg-surface-2)] text-[var(--text-primary)]" : "txt-muted hover:text-[var(--text-primary)]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      />

      <div className="px-4 sm:px-8 py-5 space-y-5 max-w-full">
        <Link to="/signals" className="text-xs txt-muted hover:underline">← Signal Feed</Link>

        {error === "notfound" && (
          <div data-testid="scanperf-notfound" className="panel p-6 text-sm txt-muted">
            Unknown scanner “{scanner}”.
          </div>
        )}
        {error === "failed" && (
          <div data-testid="scanperf-error" className="panel p-6 text-sm txt-muted">
            Could not load the scanner performance. <button type="button" className="underline" onClick={load}>Retry</button>
          </div>
        )}
        {loading && !data && !error && <div className="overline animate-pulse">Loading…</div>}

        {s && (
          <>
            <div data-testid="scanperf-tiles" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile testid="scanperf-tile-signals" label="Signals" value={s.signals}
                    sub={data.days ? `last ${data.days} days` : `all kept (${data.retention_days} days)`} />
              <Tile testid="scanperf-tile-executed" label="Executed by you" value={s.executed?.any ?? 0}
                    sub={`paper ${s.executed?.paper ?? 0} · live ${s.executed?.live ?? 0} · not executed ${s.not_executed ?? 0}`} />
              <Tile testid="scanperf-tile-outcomes" label="Target / Stop" value={`${o.target_hit ?? 0} / ${o.stop_hit ?? 0}`}
                    sub={`expired ${o.expired ?? 0} · open ${o.open ?? 0} · superseded ${o.superseded ?? 0}${o.other ? ` · other ${o.other}` : ""}`} />
              <Tile testid="scanperf-tile-winrate" label="Paper win rate"
                    value={p.trade_count ? `${Number(p.win_rate_pct).toFixed(1)}%` : DASH}
                    sub={`${p.trade_count ?? 0} closed trades · ${p.win_count ?? 0}W ${p.loss_count ?? 0}L`} />
              <Tile testid="scanperf-tile-avg" label="Avg win / loss"
                    value={p.trade_count ? `${fmtMoney(p.avg_win)} / ${fmtMoney(p.avg_loss)}` : DASH}
                    valueClass="text-[var(--text-primary)] text-base sm:text-lg" />
              <Tile testid="scanperf-tile-expectancy" label="Expectancy / trade"
                    value={p.trade_count ? fmtMoney(p.expectancy_per_trade) : DASH}
                    valueClass={tone(p.expectancy_per_trade)}
                    sub={p.trade_count ? `profit factor ${p.profit_factor} · sharpe ${p.sharpe}` : undefined} />
              <Tile testid="scanperf-tile-paper-pnl" label="Paper P&L"
                    value={p.trade_count ? fmtMoney(p.total_pnl) : DASH} valueClass={tone(p.total_pnl)}
                    sub={`${p.signals_with_pnl ?? 0} signals with closed trades`} />
              <Tile testid="scanperf-tile-live-pnl" label="Live P&L (booked)"
                    value={lv.trade_count ? fmtMoney(lv.total_pnl) : DASH} valueClass={tone(lv.total_pnl)}
                    sub={`${lv.trade_count ?? 0} booked live exits`} />
            </div>

            {data.truncated && (
              <div className="text-xs text-amber-400">Showing the newest {s.signals} signals of this period only.</div>
            )}

            <div data-testid="scanperf-scroll" className="panel overflow-x-auto">
              <table className="w-full min-w-[860px] text-xs">
                <thead className="border-b border-[var(--border)]">
                  <tr>
                    {COLUMNS.map(({ h, sort: key }) => {
                      if (!key) return <th key={h} className="px-3 py-2.5 text-left overline whitespace-nowrap">{h}</th>;
                      const dir = sort.key === key ? sort.order : null;
                      return (
                        <th key={h} aria-sort={dir === "desc" ? "descending" : dir === "asc" ? "ascending" : "none"}
                            className="px-3 py-2.5 text-left overline whitespace-nowrap">
                          <button type="button" data-testid={`scanperf-sort-${key}`} onClick={() => changeSort(key)}
                                  className="overline hover:text-[var(--text-primary)] inline-flex items-center gap-1">
                            {h}<span aria-hidden="true">{dir === "desc" ? "↓" : dir === "asc" ? "↑" : "↕"}</span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {(data.items || []).length === 0 && (
                    <tr><td colSpan={COLUMNS.length} className="px-3 py-6 text-center txt-muted">No signals in this period.</td></tr>
                  )}
                  {(data.items || []).map((r) => {
                    const bull = r.direction === "BULLISH";
                    return (
                      <tr key={r.id} data-testid={`scanperf-row-${r.id}`} className="hover:bg-[var(--bg-surface-2)]/50">
                        <td className="px-3 py-2 font-mono-data whitespace-nowrap txt-secondary">{fmtIst(r.created_at)}</td>
                        <td className="px-3 py-2 font-section text-[var(--text-primary)] whitespace-nowrap">{r.symbol || DASH}</td>
                        <td className="px-3 py-2 font-mono-data whitespace-nowrap txt-secondary">{instrumentText(r)}</td>
                        <td className={`px-3 py-2 font-section whitespace-nowrap ${bull ? "txt-profit" : "txt-loss"}`}>
                          {r.direction ? (bull ? "Bull" : "Bear") : DASH}
                        </td>
                        <td className="px-3 py-2 font-mono-data whitespace-nowrap txt-secondary">
                          {fmtPrice(r.entry_price)} / {fmtPrice(r.stop_loss)} / {fmtPrice(r.target_1)}
                        </td>
                        <td className="px-3 py-2 font-mono-data whitespace-nowrap">
                          {r.confidence_score != null ? `${Math.round(Number(r.confidence_score) * 100)}%` : DASH}
                        </td>
                        <td className={`px-3 py-2 font-section whitespace-nowrap ${OUTCOME_TONE[r.outcome] || "txt-muted"}`}
                            title={r.status ? `status: ${r.status}` : undefined}>
                          {r.outcome === "other" ? (r.status || "Other") : (OUTCOME_LABELS[r.outcome] || DASH)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap txt-secondary">{executedText(r.executed)}</td>
                        <td data-testid={`scanperf-pnl-paper-${r.id}`}
                            className={`px-3 py-2 font-mono-data whitespace-nowrap ${r.pnl_paper == null ? "txt-muted" : tone(r.pnl_paper)}`}>
                          {fmtMoney(r.pnl_paper)}
                        </td>
                        <td data-testid={`scanperf-pnl-live-${r.id}`}
                            className={`px-3 py-2 font-mono-data whitespace-nowrap ${r.pnl_live == null ? "txt-muted" : tone(r.pnl_live)}`}>
                          {fmtMoney(r.pnl_live)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="txt-muted" data-testid="scanperf-pageinfo">
                Page {data.page} of {data.pages} · {data.total} signals
              </span>
              <div className="flex gap-2">
                <button type="button" data-testid="scanperf-prev" disabled={page <= 1 || loading}
                        onClick={() => setPage((n) => Math.max(1, n - 1))}
                        className="px-3 py-1.5 border border-[var(--border)] rounded-sm disabled:opacity-40">Prev</button>
                <button type="button" data-testid="scanperf-next" disabled={page >= (data.pages || 1) || loading}
                        onClick={() => setPage((n) => n + 1)}
                        className="px-3 py-1.5 border border-[var(--border)] rounded-sm disabled:opacity-40">Next</button>
              </div>
            </div>
            <div className="text-[10px] txt-muted">
              Outcomes are the signal lifecycle&apos;s own (target 1 / stop touched, expired, superseded). P&amp;L is yours:
              closed paper trades and booked live exits linked to each signal. Signals are kept {data.retention_days} days.
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
